import { STAGES } from './enums.js';
import { stagesInvesting, stagesLeading } from './fundStages.js';
import { SECTOR_TAGS } from './sectorTaxonomy.js';
import { throughWords } from './whoKnows.js';
import { impliedChequeDisplay, statedChequeDisplay, } from './chequeDisplay.js';
/** "€1M – €4M (est. from 7 rounds)"; empty where the fund states a cheque or the rounds are too few. */
export function formatImpliedCheque(fund) {
    return impliedChequeDisplay(fund)?.text ?? '';
}
export const STAGE_LABELS = {
    pre_seed: 'Pre-seed',
    seed: 'Seed',
    series_a: 'Series A',
    series_b: 'Series B',
    series_c_plus: 'Series C+',
};
/** These are also what a paste reads back, so parseLeadStatus knows them. */
export const LEAD_LABELS = {
    leads: 'Leads',
    follow_only: 'Follow only',
    selective: 'Selective',
    unknown: 'Unknown',
};
/**
 * The stated cheque in its own currency: "USD 1M–50M", which is also a string
 * parseCheque reads straight back, so it is what an editor starts from. What a
 * person reads is `statedChequeDisplay`.
 */
export function formatCheque(fund) {
    if (fund.chequeMin === null && fund.chequeMax === null)
        return '';
    const currency = fund.chequeCurrency ?? '';
    if (fund.chequeMin === null)
        return `${currency} up to ${amount(fund.chequeMax ?? 0)}`.trim();
    // A null max against a real min is the sheet's "$10M+".
    const range = fund.chequeMax === null
        ? `${amount(fund.chequeMin)}+`
        : `${amount(fund.chequeMin)}–${amount(fund.chequeMax)}`;
    return `${currency} ${range}`.trim();
}
function amount(value) {
    if (value >= 1_000_000)
        return `${trim(value / 1_000_000)}M`;
    if (value >= 1_000)
        return `${trim(value / 1_000)}K`;
    return String(value);
}
function trim(value) {
    return value.toFixed(1).replace(/\.0$/, '');
}
function stageNames(stages) {
    return stages.map((stage) => STAGE_LABELS[stage]).join(', ');
}
/**
 * Saved and derived sectors together: the taxonomy's tags first, in taxonomy
 * order, then saved values the taxonomy does not know, in their own order.
 */
export function mergedSectors(fund) {
    const present = new Set([...fund.sectors, ...fund.derivedSectors].map((sector) => sector.toLowerCase()));
    const tags = SECTOR_TAGS.filter((tag) => present.has(tag.toLowerCase()));
    const known = new Set(tags.map((tag) => tag.toLowerCase()));
    const legacy = fund.sectors.filter((sector) => !known.has(sector.toLowerCase()));
    return [...tags, ...legacy];
}
/** Region names in deal order: what the rounds show, with no shares. */
export function regionNames(fund) {
    return fund.observedRegions.map((entry) => entry.region);
}
/** Everything that came from rounds, for whoever wants the provenance: "stages: Series A · leads: led 2 of 7 · sectors: Fintech". */
export function observedProvenance(fund) {
    const parts = [];
    if (fund.observedStages.length > 0) {
        const stages = STAGES.filter((stage) => fund.observedStages.includes(stage));
        parts.push(`stages: ${stages.map((stage) => STAGE_LABELS[stage]).join(', ')}`);
    }
    if (fund.observedRounds > 0) {
        parts.push(`leads: led ${fund.ledRounds} of ${fund.observedRounds}`);
    }
    if (fund.derivedSectors.length > 0)
        parts.push(`sectors: ${fund.derivedSectors.join(', ')}`);
    if (fund.observedRegions.length > 0)
        parts.push(`regions: ${regionNames(fund).join(', ')}`);
    return parts.join(' · ');
}
/**
 * One rendering of a fund column as text, shared by the grid and by every
 * export of it. A list column is joined with ", " so the cell reads as a
 * sentence in Sheets rather than as a machine-separated field.
 */
export const FUND_CSV_COLUMNS = {
    name: { title: 'Fund', text: (fund) => fund.name },
    tier: { title: 'Tier', text: (fund) => (fund.tier === null ? '' : String(fund.tier)) },
    stagesInvests: { title: 'Stages (invests)', text: (fund) => stageNames(stagesInvesting(fund)) },
    stagesLeads: { title: 'Stages (leads)', text: (fund) => stageNames(stagesLeading(fund)) },
    hqCountries: { title: 'HQ', text: (fund) => fund.hqCountries.join(', ') },
    regions: { title: 'Regions', text: (fund) => regionNames(fund).join(', ') },
    sectors: { title: 'Sectors', text: (fund) => mergedSectors(fund).join(', ') },
    observed: { title: 'Observed', text: observedProvenance },
    owners: { title: 'Owners (Project A)', text: (fund) => fund.owners.join(', ') },
    whoKnows: {
        title: 'Who knows whom',
        text: (fund) => fund.whoKnows
            .map((edge) => {
            const pair = `${edge.investorName} · ${edge.memberName}`;
            return edge.through == null ? pair : `${pair} (${throughWords(edge.through)})`;
        })
            .join(', '),
    },
    notableInvestments: {
        title: 'Notable investments',
        text: (fund) => fund.notableInvestments.join(', '),
    },
    cheque: { title: 'Cheque (€)', text: (fund) => statedChequeDisplay(fund)?.text ?? '' },
    chequeEstimate: { title: 'Cheque (estimated)', text: formatImpliedCheque },
    website: { title: 'Website', text: (fund) => fund.website ?? '' },
    notes: { title: 'Notes', text: (fund) => fund.notes ?? '' },
};
/** The order the grid shows and every export writes. */
export const FUND_CSV_ORDER = [
    'name',
    'tier',
    'stagesInvests',
    'stagesLeads',
    'hqCountries',
    'regions',
    'sectors',
    'owners',
    'whoKnows',
    'notableInvestments',
    'cheque',
    'chequeEstimate',
    'website',
    'notes',
];
/** RFC 4180: a field carrying a comma, a quote or a newline is quoted, and a
 * quote inside it is doubled. That is what Sheets expects on open. */
function field(value) {
    return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}
export function toCsv(headers, rows) {
    return [headers, ...rows].map((row) => row.map(field).join(',')).join('\r\n');
}
/** The whole fund list, in the grid's column order. */
export function fundsToCsv(funds) {
    const columns = FUND_CSV_ORDER.map((id) => FUND_CSV_COLUMNS[id]);
    return toCsv(columns.map((column) => column.title), funds.map((fund) => columns.map((column) => column.text(fund))));
}
//# sourceMappingURL=fundCsv.js.map